import dayjs from "dayjs";
import { FC } from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import useSuperAdmin from "../users/permissions/useSuperAdmin";
import { BADGE_COLOR, ResolvedAuthor, resolveAuthor } from "./resolveAuthor";

// Kept here so existing importers keep working; the logic lives in resolveAuthor.ts.
export { SYSTEM_USER_ID, formatAuditAuthorName } from "./resolveAuthor";

interface AuditAuthorProps {
  createdById?: number;
  createdByName: string;
  createdByLastName: string;
  updatedById?: number;
  updatedByName: string;
  updatedByLastName: string;
  insertDate: string;
  updateDate: string;
  // When both are given, the stamp links to the entity's change history (SuperAdmins only).
  // `entityName` is the backend's CLR name, e.g. `Trip`, `Line`, `Operator`.
  entityName?: string;
  entityId?: number;
}

const AuthorBadge: FC<{ author: ResolvedAuthor | null; tooltipId: string }> = ({ author, tooltipId }) =>
  author ? (
    <>
      <div
        data-tooltip-content={author.label}
        data-tooltip-id={tooltipId}
        className={`${BADGE_COLOR[author.kind]} ring-1 ring-gray-300
        font-medium h-6 w-6 rounded-full flex justify-center items-center mx-2 my-1 cursor-pointer`}
      >
        {author.initial}
      </div>
      <Tooltip id={tooltipId} className="z-50" />
    </>
  ) : (
    <span className="mx-2"> - </span>
  );

const AuditAuthor: FC<AuditAuthorProps> = ({
  insertDate,
  createdById,
  createdByName,
  createdByLastName,
  updatedById,
  updatedByName,
  updateDate,
  updatedByLastName,
  entityName,
  entityId
}) => {
  const isSuperAdmin = useSuperAdmin();
  const createdBy = resolveAuthor(createdById, createdByName, createdByLastName);
  const updatedBy = resolveAuthor(updatedById, updatedByName, updatedByLastName);
  const hasUpdate = updateDate !== null && updateDate !== undefined;

  const stamp = (
    <span className="flex flex-col ">
      <span className="flex items-center text-gray-600">
        Created by:
        <AuthorBadge author={createdBy} tooltipId="createdBy" />
        At {insertDate ? dayjs(insertDate).format("YYYY/MM/DD") : null}
      </span>
      {hasUpdate ? (
        <span className="flex items-center text-gray-600">
          Updated by:
          <AuthorBadge author={updatedBy} tooltipId="updatedBy" />
          At {updateDate ? dayjs(updateDate).format("YYYY/MM/DD") : null}
        </span>
      ) : null}
    </span>
  );

  // The stamp says who touched the row last; the history says what every change was. Link the
  // two where the caller knows which entity this is, for the people allowed to read the history.
  if (!entityName || !entityId || !isSuperAdmin) return stamp;
  return (
    <Link
      to={`/change-history?entityName=${encodeURIComponent(entityName)}&entityId=${entityId}`}
      state={["Historiku i ndryshimeve"]}
      title="Shiko historikun e ndryshimeve"
      className="block rounded hover:bg-gray-50 hover:underline decoration-dotted"
    >
      {stamp}
    </Link>
  );
};

export default AuditAuthor;
